import { type Pagination } from 'utils/generic';
import type { OfferData, DraftOfferData } from './model.ts';
import type { UnpackedOfferData } from 'service/unpacked/model';
import { DEFAULT_OFFERS_VALUE } from 'signals/offers.ts';
import { searchUnpackedOffers, type SearchUnpackedOffersParams } from 'service/unpacked/resource.ts';
import {
	getOffersStatistics,
	getRelatedOffers,
	getOffersSearch,
	type GetOffersResponse,
	type GetMixedOffersSearchResponse,
	type GetOffersParams,
	getCreatePage,
} from './resource.ts';
import { isUnpackedOffer } from 'utils/isOfferType';
import { remoteFilterToUnpackedOffersFilter } from 'service/unpacked/utils.ts';
import {
	UNPACKED_OFFERS_PER_PAGE,
	UNPACKED_OFFERS_MODIFIER,
	OFFERS_WITHOUT_ADS_PER_PAGE,
	OFFERS_WITHOUT_UNPACKED_PER_PAGE,
	MAX_OFFERS_PAGE,
} from 'utils/constants.ts';
import { normalizeToCamelCase } from 'utils/normalize.ts';

export async function getOtherOffers(
	offerId: string | number,
	limit: number = 7,
	headers?: HeadersInit,
	abortController?: AbortController,
): Promise<GetOffersResponse> {
	const offerIdString = String(offerId);

	const offerRelatedParams = {
		id: offerIdString,
		limit: String(limit + 1),
	};

	const otherOffers = await getRelatedOffers(offerRelatedParams, headers, abortController).catch(() => undefined);

	if (!otherOffers) return DEFAULT_OFFERS_VALUE;

	// - odstranuji z dalsich inzeratu aktualne zobrazeny inzerat
	const newOfferList = otherOffers.results.filter((offer) => String(offer.id) !== offerIdString);

	// - pokud v seznamu aktualne zobrazeny inzerat neni, tak odeberu posledni inzerat ze seznamu
	if (newOfferList.length > limit) {
		newOfferList.pop();
	}

	return { ...otherOffers, results: newOfferList };
}

export async function getOtherSellerOffers(
	offer: OfferData | UnpackedOfferData,
	headers?: HeadersInit,
	abortController?: AbortController,
): Promise<{
	pagination: Pagination;
	results: (OfferData | UnpackedOfferData)[];
}> {
	const offerId = offer.id;
	let otherOffers;

	if (isUnpackedOffer(offer)) {
		otherOffers = await searchUnpackedOffers(
			{ shop_id: offer.shop.id, limit: String(4) },
			headers,
			abortController,
		).catch(() => undefined);
	} else if (offer.isOfferMirroring && offer.premise) {
		const { id: premiseId } = offer.premise;
		otherOffers = await getOffersSearch(
			{
				premise_id: String(premiseId),
				limit: String(7),
			},
			headers,
			abortController,
		).catch(() => undefined);
	} else if (offer.user?.id) {
		otherOffers = await getOffersSearch(
			{
				user_id: String(offer.user.id),
				limit: String(7),
			},
			headers,
			abortController,
		).catch(() => undefined);
	}

	if (!otherOffers) return DEFAULT_OFFERS_VALUE;

	// - odstranuji z dalsich inzeratu aktualne zobrazeny inzerat
	const newOfferList = otherOffers.results.filter((offer) => String(offer.id) !== String(offerId));

	// - pokud v seznamu aktualne zobrazeny inzerat neni, tak odeberu posledni inzerat ze seznamu
	if (newOfferList.length > 6) {
		newOfferList.pop();
	}

	return { ...otherOffers, results: newOfferList };
}

export async function getOfferStatistics(offer: OfferData, headers?: HeadersInit) {
	if (!offer.user?.id) return;

	const result = await getOffersStatistics({ item_ids: String(offer.id) }, headers).catch(() => undefined);
	return result && result[offer.id];
}

/**
 * Searches offers and includes unpacked offers if unpackedOffersFilter is provided and can be included. The unpacked
 * offers filter is automatically stripped out any filters that are not relevant to unpacked offers and sanitizes the
 * rest for your convenience.
 *
 * If you do not want to search for unpacked offers, simply do not provide the unpackedOffersFilter or
 * use {@link getOffersSearch}.
 * @param offersFilter
 * @param unpackedOffersFilter
 * @param headers
 */
export async function getMixedOffersSearch(
	offersFilter: GetOffersParams,
	unpackedOffersFilter?: SearchUnpackedOffersParams,
	headers?: HeadersInit,
): Promise<GetMixedOffersSearchResponse> {
	if (!unpackedOffersFilter || !['zbozi', undefined].includes(unpackedOffersFilter?.sort)) {
		return await getOffersSearch(offersFilter, headers);
	}

	let unpackedTotal;

	try {
		const unpackedResponse = await searchUnpackedOffers(
			remoteFilterToUnpackedOffersFilter({ ...unpackedOffersFilter, limit: '1', offset: '0' }),
			headers,
		);

		unpackedTotal = unpackedResponse?.pagination?.total || 0;
	} catch (error) {
		console.error(error);
		return await getOffersSearch(offersFilter, headers);
	}

	const offersPage = offersFilter.offset
		? Math.floor(Number(offersFilter.offset) / Number(offersFilter.limit) || 1) + 1
		: 1;
	const unpackedOffset = (offersPage - 1) * UNPACKED_OFFERS_PER_PAGE;

	const notEnoughUnpacked = unpackedTotal === 0 || unpackedTotal <= unpackedOffset;

	const offersOffset = Number(offersFilter.offset) - Math.min(unpackedOffset, unpackedTotal);
	const [offersPromise, unpackedOffersPromise] = await Promise.allSettled([
		getOffersSearch({ ...offersFilter, offset: String(offersOffset) }, headers),
		notEnoughUnpacked
			? Promise.reject(new Error('No unpacked offers available'))
			: searchUnpackedOffers(
					remoteFilterToUnpackedOffersFilter({ ...unpackedOffersFilter, offset: String(unpackedOffset) }),
					headers,
				),
	]);

	if (offersPromise.status === 'rejected') {
		throw offersPromise.reason instanceof Error ? offersPromise.reason : new Error(offersPromise.reason);
	}

	const offers = offersPromise.value;
	const unpackedOffers =
		unpackedOffersPromise?.status === 'fulfilled' ? unpackedOffersPromise.value : { results: [] };

	let finalOffers: (OfferData | UnpackedOfferData)[] = [];

	let unpackedPointer = 0;
	let offersCounter = 0;
	let finalPointer = 0;
	let positionSelector = UNPACKED_OFFERS_MODIFIER - 1;
	if (unpackedOffers.results.length > 0 && offers.results.length > 0) {
		for (
			;
			offersCounter < offers.results.length && finalOffers.length < OFFERS_WITHOUT_ADS_PER_PAGE - 1;
			offersCounter++
		) {
			const offer = offers.results[offersCounter] as OfferData;

			const unpackedOffer = unpackedOffers.results[unpackedPointer];
			if (unpackedOffer && finalPointer % UNPACKED_OFFERS_MODIFIER === positionSelector) {
				unpackedPointer++;
				finalOffers.push(offer);
				finalOffers.push(unpackedOffer);
				finalPointer++;
				positionSelector = positionSelector === 3 ? UNPACKED_OFFERS_MODIFIER - 1 : positionSelector - 1;
			} else {
				finalOffers.push(offer);
			}
			finalPointer++;
		}

		const unpackedOffer = unpackedOffers.results[unpackedPointer];
		if (unpackedOffer) {
			unpackedPointer++;
			finalOffers.push(unpackedOffer);
		} else if (offersCounter < offers.results.length) {
			finalOffers.push(offers.results[offersCounter] as OfferData);
		}
	} else {
		finalOffers = offers.results;
		offersCounter = offers.results.length;
	}

	const totalOffersPages = Math.ceil(offers.pagination.total / OFFERS_WITHOUT_UNPACKED_PER_PAGE);
	const totalWithUnpacked = Math.min(
		(offers.pagination.total || 0) + Math.min(totalOffersPages * UNPACKED_OFFERS_PER_PAGE, unpackedTotal),
		(MAX_OFFERS_PAGE - 1) * OFFERS_WITHOUT_UNPACKED_PER_PAGE,
	);

	return {
		results: finalOffers.map((offer) => ({ ...offer, loadedOnPageNumber: offersPage })),
		pagination: {
			limit: offers.pagination.limit || 0,
			total: offers.pagination.total || 0,
			totalWithUnpacked,
			offset: offersCounter,
		},
	};
}

export async function getPrefilledDataForNewOffer(headers?: HeadersInit): Promise<DraftOfferData | null> {
	const createPageData = await getCreatePage(headers);

	if (!createPageData) return null;

	const tmpOffer: any = {};

	createPageData.sections.forEach((section) => {
		section.elements.forEach(({ name, value }) => {
			tmpOffer[name] = value;
		});
	});

	return normalizeToCamelCase(tmpOffer) as DraftOfferData;
}
